/**
 * Recolours the "Sign in with shop" button inside Shopify's <shopify-account>
 * component. The button mounts inside a nested shadow DOM when the account sheet
 * opens, so we poll for shadow roots and reinject on the component's "open" event.
 */
(function () {
  const STYLE_ID = 'wnbs-shopify-account-branding';
  const ACCENT = '#00a3da';
  const ACCENT_HOVER = '#18c2fc';

  const STYLE_TEXT = `
    button[data-testid="login-button"],
    .bg-purple-primary {
      background-color: ${ACCENT} !important;
      border-color: ${ACCENT} !important;
    }
    button[data-testid="login-button"]:enabled:hover,
    .hover\\:enabled\\:bg-purple-d0:enabled:hover {
      background-color: ${ACCENT_HOVER} !important;
      border-color: ${ACCENT_HOVER} !important;
    }
  `;

  const watched = new WeakSet();

  function collectShadowRoots(root, seen) {
    if (!root) return [];
    seen = seen || new Set();
    if (seen.has(root)) return [];
    seen.add(root);

    const roots = [root];
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) {
        roots.push(...collectShadowRoots(el.shadowRoot, seen));
      }
    });
    return roots;
  }

  function injectIntoRoot(root) {
    let style = root.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      root.appendChild(style);
    }
    style.textContent = STYLE_TEXT;
  }

  function injectStyles(account) {
    if (!account?.shadowRoot) return 0;

    const roots = collectShadowRoots(account.shadowRoot);
    roots.forEach(injectIntoRoot);
    return roots.length;
  }

  function scheduleApply(apply) {
    apply();
    requestAnimationFrame(apply);
    [50, 150, 300, 600, 1000, 2000].forEach((delay) => {
      setTimeout(apply, delay);
    });
  }

  function watchAccount(account) {
    if (watched.has(account)) return;
    watched.add(account);

    const shadowObservers = [];
    let pollId = null;

    const apply = () => {
      if (!account.shadowRoot) return;
      injectStyles(account);
      attachShadowObservers();
    };

    const attachShadowObservers = () => {
      shadowObservers.forEach((observer) => observer.disconnect());
      shadowObservers.length = 0;
      if (!account.shadowRoot) return;

      collectShadowRoots(account.shadowRoot).forEach((root) => {
        const observer = new MutationObserver(apply);
        observer.observe(root, { childList: true, subtree: true });
        shadowObservers.push(observer);
      });
    };

    const waitForShadowRoot = () => {
      if (!account.shadowRoot) return;
      if (pollId) {
        clearInterval(pollId);
        pollId = null;
      }
      scheduleApply(apply);
    };

    account.addEventListener('open', () => scheduleApply(apply));

    pollId = setInterval(waitForShadowRoot, 100);
    setTimeout(() => {
      if (pollId) clearInterval(pollId);
    }, 30000);

    waitForShadowRoot();
  }

  function init() {
    document.querySelectorAll('shopify-account').forEach(watchAccount);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.nodeName === 'SHOPIFY-ACCOUNT') watchAccount(node);
          node.querySelectorAll?.('shopify-account').forEach(watchAccount);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  function start() {
    init();
  }

  if (customElements.whenDefined) {
    customElements.whenDefined('shopify-account').then(start).catch(start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
